import { useState } from 'react';
import { 
  Card, 
  Title, 
  Select, 
  Stack, 
  Divider, 
  Text, 
  Switch, 
  Group, 
  Button,
  Tooltip,
  SimpleGrid,
  Box,
  TextInput,
  ColorSwatch,
  useMantineTheme,
  rem
} from '@mantine/core';
import { mockTeams } from '../../types/team';
import { 
  ShieldCheckIcon, 
  LockClosedIcon, 
  DocumentIcon, 
  UserGroupIcon,
  Cog6ToothIcon,
  BellAlertIcon
} from '@heroicons/react/24/outline';
import { useColors } from '../../hooks/useColors';

interface TeamSettingsProps {
  onSaveSettings: (teamId: string, settings: TeamSettingsValues) => void;
}

interface TeamSettingsValues {
  accessLevel: string;
  notificationsEnabled: boolean;
  canManageMembers: boolean;
  canCreateDocuments: boolean;
  color: string;
  notificationEmail: string;
}

export function TeamSettings({ onSaveSettings }: TeamSettingsProps) {
  const colorPalette = useColors();
  const [selectedTeam, setSelectedTeam] = useState<string>('');
  const [settings, setSettings] = useState<TeamSettingsValues>({
    accessLevel: 'team_only',
    notificationsEnabled: true,
    canManageMembers: false,
    canCreateDocuments: true,
    color: colorPalette.getBrandColor('teamBlue'),
    notificationEmail: ''
  });
  const theme = useMantineTheme();

  // Transform teams for Select component
  const teamOptions = mockTeams.map(team => ({
    value: team.id,
    label: team.name
  }));

  // Get current team
  const currentTeam = mockTeams.find(team => team.id === selectedTeam);

  // Access level options
  const accessLevelOptions = [
    { value: 'team_only', label: 'Team members only' },
    { value: 'department', label: 'Department members' },
    { value: 'company', label: 'Company-wide' }
  ];

  // Handle setting changes
  const handleSettingChange = (key: keyof TeamSettingsValues, value: any) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // Handle save settings
  const handleSaveSettings = () => {
    if (!selectedTeam) return;
    onSaveSettings(selectedTeam, settings);
  };

  // Color options
  // Only use colors that are guaranteed to be in the theme and add safety checks
  const safeColorPalettes = ['blue', 'red', 'green', 'yellow', 'gray'];
  const colors = safeColorPalettes
    .map(colorName => {
      const colorPalette = theme.colors[colorName as keyof typeof theme.colors];
      return colorPalette ? colorPalette[6] : undefined;
    })
    .filter(Boolean) as string[];

  return (
    <Card shadow="sm" padding="lg" radius="md" withBorder>
      <Title order={2} size="h3" mb="md">Team Settings</Title>

      <Stack gap="md">
        <Select
          label="Select a Team"
          placeholder="Choose a team to configure"
          data={teamOptions}
          value={selectedTeam}
          onChange={(value) => {
            if (value) setSelectedTeam(value);
          }}
          searchable
          clearable
        />

        {selectedTeam && (
          <>
            <Text fw={500} mb="xs" mt="md">
              Settings for {currentTeam?.name}
            </Text>

            <Divider 
              label={
                <Group gap="xs">
                  <ShieldCheckIcon width={16} />
                  <Text>Access Control</Text>
                </Group>
              } 
              labelPosition="center" 
            />

            <Select
              label="Team Visibility"
              description="Who can see this team and its members"
              placeholder="Select access level"
              data={accessLevelOptions}
              value={settings.accessLevel}
              onChange={(value) => {
                if (value) handleSettingChange('accessLevel', value);
              }}
              leftSection={<LockClosedIcon width={16} />}
            />

            <SimpleGrid cols={2}>
              <Switch
                label="Members can manage the team"
                description="Allow team members to add/remove other members"
                checked={settings.canManageMembers}
                onChange={(event) => handleSettingChange('canManageMembers', event.currentTarget.checked)}
              />

              <Switch
                label="Members can create documents"
                description="Allow team members to create and share team documents"
                checked={settings.canCreateDocuments}
                onChange={(event) => handleSettingChange('canCreateDocuments', event.currentTarget.checked)}
              />
            </SimpleGrid>

            <Divider 
              label={
                <Group gap="xs">
                  <BellAlertIcon width={16} />
                  <Text>Notifications</Text>
                </Group>
              } 
              labelPosition="center" 
            />

            <Switch
              label="Enable team notifications"
              description="Send notifications when team membership changes or documents are shared"
              checked={settings.notificationsEnabled}
              onChange={(event) => handleSettingChange('notificationsEnabled', event.currentTarget.checked)}
            />

            {settings.notificationsEnabled && (
              <TextInput
                label="Notification Email"
                description="Email address for team notifications"
                placeholder="team@company.com"
                value={settings.notificationEmail}
                onChange={(event) => handleSettingChange('notificationEmail', event.currentTarget.value)}
              />
            )}

            <Divider 
              label={
                <Group gap="xs">
                  <Cog6ToothIcon width={16} />
                  <Text>Appearance</Text>
                </Group>
              } 
              labelPosition="center" 
            />

            <Stack gap="xs">
              <Text size="sm" fw={500}>Team Color</Text>
              <Text size="xs" c="dimmed">This color will be used for the team in various UI elements</Text>
              <Group gap="xs">
                {colors.map((color) => (
                  <Tooltip key={color} label={color}>
                    <ColorSwatch
                      color={color}
                      size={30}
                      style={{ 
                        cursor: 'pointer', 
                        border: settings.color === color ? 
                          `2px solid ${theme.colors.gray[7]}` : 
                          '1px solid transparent'
                      }}
                      onClick={() => handleSettingChange('color', color)}
                    />
                  </Tooltip>
                ))}
              </Group>
            </Stack>

            <Button 
              mt="lg" 
              onClick={handleSaveSettings}
              leftSection={<Cog6ToothIcon width={16} />}
            >
              Save Settings
            </Button>
          </>
        )}
      </Stack>
    </Card>
  );
} 
